import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  TextField,
  Container,
  Stack,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  TablePagination,
  Card,
  Radio,
} from '@mui/material';
import { Unstable_Popup as BasePopup } from '@mui/base/Unstable_Popup';
import { styled } from '@mui/system';
import { ThemeProvider } from '@mui/material/styles';
import { EntityFilterPagination, theme } from './TableStyles';
import {
  PopAnimation,
  PopupSuccessBody,
  PopupErrorBody,
  POPUP_DURATION_SUCCESS,
  POPUP_DURATION_ERROR,
} from './popUpStyles';
import API from '../api';
import { DefaultOutlinedButton, DefaultContainedButton } from './Buttons';
import { CustomAutocomplete, CustomTextField } from './StyledInputs';
import { HttpErrorHandler } from './ErrorHandler';
import { LoadingUI } from './LayoutStyles';
import { entityTypeLabel } from './EntityTypeLabel';

// custom popup style
const Popup = styled(BasePopup)`
  z-index: 1400;
`;

export const EntitiesDialog = ({ openDialog, closeDialog, outputEntity, context }) => {
  /* ---------------------------------- Popup --------------------------------- */
  const [anchor, setAnchor] = useState(null);
  const [open, setOpen] = useState(false);
  const [popUpText, setPopUpText] = useState('');
  const [popupSuccess, setPopupSuccess] = useState('');

  const showPopup = (success, text) => {
    const totalDelay = success ? POPUP_DURATION_SUCCESS : POPUP_DURATION_ERROR;
    const animationDelay = 400;

    setPopupSuccess(success);
    setOpen(true);
    setPopUpText(text);

    setTimeout(() => {
      setOpen(false);

      setTimeout(() => {}, animationDelay);
    }, totalDelay);
  };

  // popup component
  const displayPopup = () => {
    return (
      <Container ref={setAnchor} sx={{ position: 'absolute', bottom: '-20px' }}>
        <Popup anchor={anchor} open={open}>
          {popupSuccess ? (
            <PopupSuccessBody sx={{ whiteSpace: 'pre-line' }}>{popUpText}</PopupSuccessBody>
          ) : (
            <PopupErrorBody>{popUpText}</PopupErrorBody>
          )}
        </Popup>
      </Container>
    );
  };

  const [countriesList, setCountriesList] = useState([]);
  const [regionsList, setRegionsList] = useState([]);
  const [provincesList, setProvincesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [eTypesList, setETypesList] = useState([]);
  const [apiCalled, setApiCalled] = useState(false);

  const [selectedEType, setSelectedEType] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedName, setSelectedName] = useState('');
  const [selectedEntity, setSelectedEntity] = useState({});

  const [entitiesList, setEntitiesList] = useState([]);

  // Get Countries and Entity Types
  useEffect(() => {
    if (openDialog) {
      API.get(`/countries`).then((response) => {
        setCountriesList(response.data);
      });

      API.get(`/etypes`).then((response) => {
        setETypesList(response.data.entity_types);
      });
    }
  }, [openDialog]);

  const handleSelectEntity = (entity) => {
    if (Object.keys(selectedEntity).length > 0) {
      if (entity.id === selectedEntity.id) {
        setSelectedEntity({});
      } else {
        setSelectedEntity(entity);
      }
    } else {
      setSelectedEntity(entity);
    }
  };

  // Set Loading Status
  useEffect(() => {
    if (countriesList.length > 0 && eTypesList.length > 0) {
      setApiCalled(true);
    }
  }, [countriesList, eTypesList]);

  //   Get Italy Default Country
  useEffect(() => {
    const italy = countriesList.find((element) => element.name === 'Italia');
    setSelectedCountry(italy);
  }, [countriesList]);

  //   Get Regions List
  useEffect(() => {
    if (selectedCountry !== null && selectedCountry !== undefined && selectedCountry !== '') {
      if (typeof selectedCountry === 'object' && Object.keys(selectedCountry).length > 0) {
        API.get(`/countries/regions`, {
          params: {
            id: selectedCountry.id,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setRegionsList(response.data);
          } else {
            setRegionsList([]);
            setProvincesList([]);
            setCitiesList([]);
          }
        });
      }
    }
  }, [selectedCountry]);

  //   Get Provinces List
  useEffect(() => {
    if (selectedRegion !== null && selectedRegion !== undefined && selectedRegion !== '') {
      if (typeof selectedRegion === 'object' && Object.keys(selectedRegion).length > 0) {
        API.get(`/regions/provinces`, {
          params: {
            region_name: selectedRegion.region_name,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setProvincesList(response.data);
          } else {
            setProvincesList([]);
            setCitiesList([]);
          }
        });
      }
    }
  }, [selectedRegion]);

  //   Get Cities List
  useEffect(() => {
    if (selectedProvince !== null && selectedProvince !== undefined && selectedProvince !== '') {
      if (typeof selectedProvince === 'object' && Object.keys(selectedProvince).length > 0) {
        API.get(`/provinces/cities`, {
          params: {
            id: selectedProvince.id,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setCitiesList(response.data);
          } else {
            setCitiesList([]);
          }
        });
      }
    }
  }, [selectedProvince]);

  const emptyField = () => {
    closeDialog();
    setSelectedEType(null);
    setSelectedProvince(null);
    setSelectedRegion(null);
    setSelectedCity(null);
    setSelectedName('');
    setSelectedEntity({});
    setApiResponseData({});
    setProvincesList([]);
    setCitiesList([]);
    setEntitiesList([]);
    setOpen(false);
  };

  const handleSubmitFilter = () => {
    if (Object.keys(selectedEntity).length > 0) {
      outputEntity(selectedEntity);
      emptyField();
    } else {
      showPopup(false, "Selezionare un'entità");
    }
  };

  const [apiRequest, setApiRequest] = useState('');
  const [apiResponseData, setApiResponseData] = useState({});

  const getFilteredEntities = (e) => {
    e.preventDefault();

    const apiCall = `/entities?filter${selectedEType ? `&entity_type_id=${selectedEType.id}` : ''}${
      selectedCity ? `&city_id=${selectedCity.id}` : ''
    }${selectedProvince ? `&province_id=${selectedProvince.id}` : ''}${
      selectedRegion ? `&region_name=${selectedRegion.region_name}` : ''
    }${selectedName.length > 0 ? `&name=${selectedName}` : ''}&context=${context}`;

    setApiRequest(apiCall);

    API.get(apiCall)
      .then((response) => {
        setApiResponseData(response.data);
        showPopup(true, 'Entità filtrate con successo');
        setPage(0);
      })
      .catch((error) => {
        showPopup(false, error.response.data.message);
      });
  };

  useEffect(() => {
    if (Object.keys(apiResponseData).length > 0) {
      setEntitiesList(apiResponseData.data);
    }
  }, [apiResponseData]);

  /* ------------------------------- Pagination ------------------------------- */
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sliceStartDisplay, setSliceStartDisplay] = useState(0);
  const [sliceEndDisplay, setSliceEndDisplay] = useState(0);

  useEffect(() => {
    if (page % 2 === 0) {
      setSliceStartDisplay(0);
      setSliceEndDisplay(10);
    } else {
      setSliceStartDisplay(10);
      setSliceEndDisplay(20);
    }
  }, [page]);

  // change page
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // change rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Dialog open={openDialog} fullWidth maxWidth={'lg'} onClose={() => {}}>
      <DialogTitle sx={{ textAlign: 'center' }}>Filtro Entità</DialogTitle>
      <DialogContent>
        {apiCalled ? (
          <>
            <form onSubmit={getFilteredEntities}>
              <Grid container spacing={3} sx={{ marginTop: '4px' }}>
                {/* Entity Type */}
                <Grid item xs={12} md={4}>
                  <CustomAutocomplete
                    options={eTypesList}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={selectedEType}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id}>
                        {option.name}
                      </Box>
                    )}
                    onChange={(e, value) => setSelectedEType(value)}
                    renderInput={(params) => <TextField {...params} label="Tipo Entità" variant="outlined" />}
                  />
                </Grid>

                {/* Country */}
                <Grid item xs={12} md={4}>
                  <CustomAutocomplete
                    readOnly
                    forcePopupIcon={false}
                    options={countriesList.filter(
                      (country) =>
                        (!selectedRegion || country.id === selectedRegion.country_id) &&
                        (!selectedProvince || country.id === selectedProvince.country_id)
                    )}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={selectedCountry}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id}>
                        {option.name}
                      </Box>
                    )}
                    onChange={(e, value) => setSelectedCountry(value)}
                    renderInput={(params) => <TextField {...params} label="Paese" variant="outlined" />}
                  />
                </Grid>

                {/* Region */}
                <Grid item xs={12} md={4}>
                  <CustomAutocomplete
                    options={regionsList.filter(
                      (region) =>
                        (!selectedProvince || region.region_name === selectedProvince.region_name) &&
                        (!selectedCountry || region.country_id === selectedCountry.id)
                    )}
                    isOptionEqualToValue={(option, value) => option.region_name === value.region_name}
                    getOptionLabel={(option) => option.region_name}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.region_name}>
                        {option.region_name}
                      </Box>
                    )}
                    onChange={(e, value) => setSelectedRegion(value)}
                    renderInput={(params) => <TextField {...params} label="Regione" variant="outlined" />}
                  />
                </Grid>

                {/* Province */}
                <Grid item xs={12} md={4}>
                  <CustomAutocomplete
                    options={provincesList.filter(
                      (province) =>
                        (!selectedRegion || province.region_name === selectedRegion.region_name) &&
                        (!selectedCity || province.id === selectedCity.province_id) &&
                        (!selectedCountry || province.country_id === selectedCountry.id)
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(option) => option.name}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id}>
                        {option.name}
                      </Box>
                    )}
                    onChange={(e, value) => setSelectedProvince(value)}
                    renderInput={(params) => <TextField {...params} label="Provincia" variant="outlined" />}
                  />
                </Grid>

                {/* City */}
                <Grid item xs={12} md={4}>
                  <CustomAutocomplete
                    options={citiesList.filter((city) => !selectedProvince || city.province_id === selectedProvince.id)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(option) => option.name}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id}>
                        {option.name}
                      </Box>
                    )}
                    onChange={(e, value) => setSelectedCity(value)}
                    renderInput={(params) => <TextField {...params} label="Comune" variant="outlined" />}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <CustomTextField
                    name="Entity-Name"
                    label="Nome Entità"
                    value={selectedName}
                    onChange={(e) => {
                      setSelectedName(e.target.value);
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Stack direction="row" justifyContent="center" alignItems="center" sx={{ width: '100%' }}>
                    <DefaultOutlinedButton customStyles={{ width: '100%' }} onClick={getFilteredEntities}>
                      Cerca Entità
                    </DefaultOutlinedButton>
                  </Stack>
                </Grid>
              </Grid>
            </form>

            <Card sx={{ marginTop: '24px' }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>Nome Entità</TableCell>
                      <TableCell>Tipo Entità</TableCell>
                      <TableCell>Regione</TableCell>
                      <TableCell>Provincia</TableCell>
                      <TableCell>Comune</TableCell>
                    </TableRow>
                  </TableHead>
                  {entitiesList.length > 0 ? (
                    <TableBody>
                      {entitiesList.slice(sliceStartDisplay, sliceEndDisplay).map((entity) => (
                        <TableRow
                          sx={{
                            backgroundColor: entity.id === selectedEntity.id ? '#d0f8dc' : 'inherit',
                            '&:hover': { backgroundColor: '#bff7d0' },
                          }}
                          hover={entity.id !== selectedEntity.id}
                          aria-checked={entity.id === selectedEntity.id}
                          key={entity.id}
                          onClick={() => handleSelectEntity(entity)}
                        >
                          <TableCell>
                            <Radio checked={entity.id === selectedEntity.id} />
                          </TableCell>
                          <TableCell>{entity.name}</TableCell>
                          {/* <TableCell>{entity.entity_type.name}</TableCell> */}
                          <TableCell>{entityTypeLabel(entity, true)}</TableCell>
                          <TableCell>{entity.province?.region_name}</TableCell>
                          <TableCell>{entity.province?.name}</TableCell>
                          <TableCell>{entity.city?.name}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  ) : (
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={6} sx={{ textAlign: 'center' }}>
                          Nessuna entità disponibile
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}

                  <TableFooter>
                    <TableRow>
                      <ThemeProvider theme={theme}>
                        <TablePagination
                          sx={{ borderBottom: 'none' }}
                          rowsPerPageOptions={[]}
                          count={Object.keys(apiResponseData).length > 0 ? apiResponseData.total : 0}
                          rowsPerPage={rowsPerPage}
                          page={page}
                          onPageChange={handleChangePage}
                          onRowsPerPageChange={handleChangeRowsPerPage}
                          ActionsComponent={(props) => (
                            <EntityFilterPagination
                              {...props}
                              apiResponseData={apiResponseData}
                              setApiResponseData={setApiResponseData}
                              lastPage={apiResponseData.last_page}
                              apiRequest={apiRequest}
                            />
                          )}
                        />
                      </ThemeProvider>
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            </Card>
          </>
        ) : (
          LoadingUI()
        )}
      </DialogContent>
      <DialogActions>
        <DefaultOutlinedButton onClick={() => emptyField()}>Chiudi</DefaultOutlinedButton>
        <DefaultContainedButton
          onClick={() => {
            handleSubmitFilter();
          }}
        >
          Seleziona
        </DefaultContainedButton>

        {displayPopup()}
      </DialogActions>
    </Dialog>
  );
};
